import React, { useEffect, useState } from "react";
import {
  Input,
  Label,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ViewModal from "@components/custom-table/ViewModel";
import {
  addBanner,
  editBanner,
  getBanner,
  getBannerCount,
} from "../services/banner-services";
import { resetError, resetState } from "../controller/banner-slice";
import { PHONE_NUMBER_MAXLENGTH, rowTypes, types } from "@constants/constants";
import Select from "react-select";
import {
  bannerSchema,
  bannerUpdateSchema,
} from "../../../helpers/validation-schema";
import { getMediaList } from "../../media/services/media-services";
import { getPostList } from "../../post/services/post-services";
import { Client } from "@helpers/helpers";
import { API_METHOD } from "@constants/constants";
import { url } from "@constants/url-constants";
import ImageFileUpload from "../../../components/custom-image/ImageFileUpload";
import CustomLabel from "../../../components/CustomLabel";
import { bannerTypes } from "../../../constants/constants";

export const AddBanner = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const bannerId = pathname.split("/")[3];
  const success = useSelector((state) => state.banner.success);
  const banner = useSelector((state) => state.banner.banners);
  const loading = useSelector((state) => state.banner.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.banner.successMessage);
  const errorMessage = useSelector((state) => state.banner.errorMessage);
  const medias = useSelector((state) => state.media.medias);
  const posts = useSelector((state) => state.post.posts);
  const [isLoading, setIsLoading] = useState(false);
  const [viewModel, setViewModel] = useState(false);
  const [fileInfo, extractFileMetadata] = ImageFileUpload();
  const options = {
    postOptions:
      posts
        ?.filter((row) => row.deleted_at === null && row.published_at !== null)
        ?.map((row) => ({ value: row._id, label: row?.title })) || [],
    mediaOptions:
      medias
        ?.filter((row) => row.deleted_at === null && row.published_at !== null)
        ?.map((row) => ({ value: row._id, label: row?.title })) || [],
    rowOptions:
      rowTypes?.map((row) => ({ value: row.id, label: row.name })) || [],
    contentTypes: types?.map((row) => ({ value: row.id, label: row.name })),
    bannerTypes:
      bannerTypes?.map((row) => ({
        value: row.id,
        label: row.name,
      })) || [],
  };
  const [bannerType, setBannerType] = useState(null);
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(route === "edit" ? bannerUpdateSchema : bannerSchema),
  });

  useEffect(() => {
    dispatch(getPostList({ pageCount: 1, limit: 100 }));
  }, []);

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getBannerCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, [route, setValue]);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getBanner(bannerId));
    }
  }, [bannerId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/banner?current_page=${pageQuery}`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);
      setIsLoading(false);
      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (banner) {
      setValue("sorting", banner?.sorting);
      setValue("media_id", banner?.media_id === null ? "" : banner?.media_id);
      setValue("image_url", banner?.image_url);
      setValue("post_id", banner?.post_id === null ? "" : banner?.post_id);
      setValue("row", banner?.row);
      setBannerType(banner?.type);
      setValue("type", banner?.type);
    }
  }, [banner, setValue]);

  useEffect(() => {
    if ((bannerType === 1 || bannerType === 2) && bannerType !== null) {
      dispatch(
        getMediaList({ pageCount: 1, limit: 100, search: "", type: bannerType })
      );
      setValue("post_id", null);
    }
    if (bannerType === 3) {
      setValue("media_id", null);
    }
  }, [bannerType, dispatch, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    let tempImageUrl = "";
    if (data.image) {
      const generatePresignedUrl = await handleImageUpload(data.image);
      if (generatePresignedUrl.imageTemp) {
        tempImageUrl = generatePresignedUrl.imageTemp;
      } else {
        //show error message
        setIsLoading(false);
        // hide loader or enable button
      }
    }

    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        row: parseInt(data.row),
        type: parseInt(data.type),
        thumbnail: {
          image_url: tempImageUrl,
          ...fileInfo,
        },
      };
      saveBannerForm(formattedData);
    } else {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        row: parseInt(data.row),
        type: parseInt(data.type),
        thumbnail: tempImageUrl
          ? {
              image_url: tempImageUrl,
              ...fileInfo,
            }
          : banner?.thumbnail,
      };
      saveBannerForm(formattedData, bannerId);
    }
  };

  const saveBannerForm = (formattedData, bannerId = "") => {
    if (bannerId) {
      dispatch(editBanner({ id: bannerId, data: formattedData }));
    } else {
      dispatch(addBanner(formattedData));
    }
  };

  const handleImageUpload = async (file) => {
    const fileExtension = {
      extensions: [file?.name.split(".").pop()?.toLowerCase()],
    };

    try {
      const response = await Client(
        API_METHOD.POST,
        url.presigned_url,
        fileExtension
      );
      const presignedUrl = response.data.data.items[0].preSignedUrl;

      const imageTemp = response.data.data.items[0].fileName;

      const saveImage = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        redirect: "follow",
      });
      if (saveImage.status === 200) {
        return { imageTemp };
      } else {
        return { type: "error", message: "Failed to Upload Image" };
      }
    } catch (error) {
      return { type: "error", message: "Failed to Upload Image" };
    }
  };

  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };

  const getCustomStyles = (field) => ({
    control: (provided, state) => ({
      ...provided,
      borderColor: errors[field] ? "red" : provided.borderColor,
      boxShadow: "none",
      "&:hover": {
        borderColor: errors[field] ? "red" : provided.borderColor,
        boxShadow: "none",
      },
    }),
  });
  const handleViewImage = () => {
    setViewModel(true);
  };
  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          {viewModel && (
            <ViewModal
              show={viewModel}
              onViewClick={handleViewImage}
              onCloseClick={() => setViewModel(false)}
              data={banner?.thumbnail?.image_url}
            />
          )}
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit Banner"
                  : route === "view"
                  ? "View Banner"
                  : "Create Banner"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onChange={(e) => handleInputChange(e, "sorting")}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Image" isRequired>
                        Image
                      </CustomLabel>

                      {route !== "add" ? (
                        <button
                          type="button"
                          className="view-button"
                          onClick={() => {
                            handleViewImage();
                          }}
                        >
                          View
                        </button>
                      ) : null}
                      <Controller
                        name="image"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <div>
                            <Input
                              type="file"
                              accept="image/jpeg,image/png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                field.onChange(e.target.files[0]);
                                extractFileMetadata(file);
                              }}
                              invalid={!!errors.image}
                            />
                            {errors.image && (
                              <FormFeedback>
                                {errors.image.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="row" isRequired>
                        Row
                      </CustomLabel>
                      <Controller
                        name="row"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={options.rowOptions}
                              placeholder="Select Row"
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={options.rowOptions.find(
                                (option) => option.value === field.value
                              )}
                              styles={getCustomStyles("row")}
                              isClearable
                            />
                            {errors.row && (
                              <FormFeedback className="d-block">
                                {errors.row.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="type" isRequired>
                        Banner Type
                      </CustomLabel>
                      <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={options.bannerTypes}
                              placeholder="Select Banner Type"
                              onChange={(selectedOption) => {
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                );
                                setBannerType(
                                  selectedOption ? selectedOption.value : null
                                );
                              }}
                              value={options.bannerTypes.find(
                                (option) => option.value === field.value
                              )}
                              styles={getCustomStyles("type")}
                              isClearable
                            />
                            {errors.type && (
                              <FormFeedback className="d-block">
                                {errors.type.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {bannerType && (
                  <Row>
                    {bannerType !== 3 ? (
                      <Col md={6}>
                        <FormGroup>
                          <CustomLabel htmlFor="Media" isRequired>
                            Media
                          </CustomLabel>
                          <Controller
                            name="media_id"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  options={options.mediaOptions}
                                  placeholder="Select Media"
                                  onChange={(selectedOption) =>
                                    field.onChange(
                                      selectedOption ? selectedOption.value : ""
                                    )
                                  }
                                  value={
                                    options.mediaOptions.find(
                                      (option) => option.value === field.value
                                    ) || null
                                  }
                                  styles={getCustomStyles("media_id")}
                                  isClearable
                                  menuPlacement="auto"
                                  minMenuHeight={150}
                                  maxMenuHeight={150}
                                  isMenuScrollable
                                />
                                {errors.media_id && (
                                  <FormFeedback className="d-block">
                                    {errors.media_id.message}
                                  </FormFeedback>
                                )}
                              </>
                            )}
                          />
                        </FormGroup>
                      </Col>
                    ) : (
                      <Col md={6}>
                        <FormGroup>
                          <CustomLabel htmlFor="Posts" isRequired>
                            Posts
                          </CustomLabel>
                          <Controller
                            name="post_id"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <>
                                <Select
                                  {...field}
                                  options={options.postOptions}
                                  placeholder="Select post"
                                  onChange={(selectedOption) =>
                                    field.onChange(
                                      selectedOption ? selectedOption.value : ""
                                    )
                                  }
                                  value={
                                    options.postOptions.find(
                                      (option) => option.value === field.value
                                    ) || null
                                  }
                                  styles={getCustomStyles("post_id")}
                                  isClearable
                                  menuPlacement="auto"
                                  minMenuHeight={150}
                                  maxMenuHeight={150}
                                  isMenuScrollable
                                />
                                {errors.post_id && (
                                  <FormFeedback className="d-block">
                                    {errors.post_id.message}
                                  </FormFeedback>
                                )}
                              </>
                            )}
                          />
                        </FormGroup>
                      </Col>
                    )}
                    {/* <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="type" isRequired>
                        Type
                      </CustomLabel>
                      <Controller
                        name="type"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={options.contentTypes}
                              placeholder="Select Type"
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={options.contentTypes.find(
                                (option) => option.value === field.value
                              )}
                              styles={getCustomStyles("type")}
                              isClearable
                            />
                            {errors.type && (
                              <FormFeedback className="d-block">
                                {errors.type.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col> */}
                  </Row>
                )}
                <Row></Row>
                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/banner?current_page=${pageQuery}`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button
                          type="submit"
                          color={isLoading ? "success" : "primary"}
                        >
                          {isLoading ? (
                            <>Loading...</>
                          ) : (
                            <>
                              {route === "edit"
                                ? "Edit Banner"
                                : "Create Banner"}
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddBanner;
